import Swal from "sweetalert2";

/* ============================================================================
   Alerts for the customer portal.

   Every alert closes itself, and every alert can also be closed by hand.

   Errors used to have no timer at all: the reasoning was that a message the
   customer has to act on -- the lapsed-policy refusal carries a phone number and
   an email address -- should not vanish before it has been read. That is still
   true, but it left the popup depending entirely on the Close button, and on a
   slow phone, or when the browser drops a click, that button is sometimes the
   only way out and it does not always take. The customer is then stuck looking
   at an alert they cannot dismiss.

   So there are now two ways out of every alert and they do not share a failure:

     1. A timer closes it on its own -- 1s for a success, 4s for an error.
     2. The Close button is still there for the customer who wants it gone now.

   The timer is the safety net, not the primary route, which is why errors get
   four seconds rather than the second and a half they carried years ago. And the
   countdown pauses while the pointer is over the popup, so somebody halfway
   through copying a phone number keeps it on screen for as long as they need.
   ============================================================================ */

/* No showClass/hideClass override here, and that is deliberate.

   This file used to set both to animate.css class names --
   `animate__animated animate__fadeOutDown` and friends. animate.css is not a
   dependency of this project and never has been: it is not in package.json, not
   imported anywhere in src, and not linked in public/index.html. So those class
   names styled nothing, and worse, they displaced `swal2-show` / `swal2-hide`,
   which are the only classes carrying real keyframes.

   That left the popup with no animation-name. Harmless on its own -- but
   App.css sets `animation-duration: 0.01ms !important` on every element under
   `prefers-reduced-motion`, and SweetAlert decides whether to wait for an
   animation by reading that duration alone (`hasCssAnimation`). A non-zero
   duration with no keyframes means it waits for an `animationend` event that
   can never fire, so the popup never gets removed. Both ways out died together,
   because the Close button and the timer both close through the same path: the
   customer was left with an alert nothing could dismiss.

   Leaving these params alone means swal2-show / swal2-hide stay on, keyframes
   and all, and the popup closes under reduced motion and normal motion alike.
   Do not reintroduce animate.css class names here without adding the actual
   dependency. */

/* Hold the countdown while the pointer is on the popup, resume when it leaves.
   Reading is the one thing the timer must not interrupt. */
const PAUSE_ON_HOVER = {
  didOpen: () => {
    const popup = Swal.getPopup();
    if (!popup) return;
    popup.addEventListener("mouseenter", Swal.stopTimer);
    popup.addEventListener("mouseleave", Swal.resumeTimer);
  },
};

/* Messages arrive from the API as plain text and some of them carry real line
   breaks -- the lapsed-policy refusal is three lines. SweetAlert's `title`
   collapses them, so errors render through `html` instead, with the text escaped
   first: it is server text, but escaping it costs nothing and means a stray
   angle bracket can never become markup. */
const toHtml = (message) => {
  const text = String(message == null ? "" : message);
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return escaped.replace(/\r?\n/g, "<br />");
};

export const showMessage = (StatusCode, Message) => {
  if (StatusCode === 200) {
    // An acknowledgement nobody needs to act on -- but it still gets a Close
    // button, so a stuck timer is never the only thing standing between the
    // customer and the screen behind it.
    Swal.fire({
      icon: "success",
      title: Message,
      timer: 1000,
      timerProgressBar: true,
      showConfirmButton: true,
      confirmButtonText: "Close",
      ...PAUSE_ON_HOVER,
    });
    return;
  }

  const isKnownError =
    StatusCode === 400 || StatusCode === 404 || StatusCode === 401;

  Swal.fire({
    icon: "error",
    html: toHtml(isKnownError ? Message : "An unexpected error occurred"),
    // Long enough to read, and it waits while the pointer is over the popup.
    timer: 4000,
    timerProgressBar: true,
    showConfirmButton: true,
    confirmButtonText: "Close",
    // A stray click outside should not take away a phone number somebody is
    // halfway through copying -- the timer and the button are the ways out.
    allowOutsideClick: false,
    ...PAUSE_ON_HOVER,
  });
};
